<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'

import utils from '@/utils.js'

const globalStore = useGlobalStore()

// Adjust this value to change the momentum decay
// Lower values (closer to 0.92) make the scrolling slower and smoother
// Higher values (closer to 0.96) make the scrolling faster and more abrupt
const momentumDeceleration = 0.95

// Threshold for stopping when velocity is low
// A smaller threshold ensures the scrolling stops only when it's very slow.
const momentumThreshold = 0.5

let shouldStartPanning,
  startPosition,
  currentPosition,
  panningTimer,
  shouldCancelPanningTimer,
  panningDelta,
  shouldPanNextFrame,
  velocity,
  momentumTimer,
  shouldCancelMomentumTimer,
  currentScroll

let unsubscribes

onMounted(() => {
  window.addEventListener('mousedown', cancelMomentum)
  window.addEventListener('mousemove', checkIfShouldStartPanning)
  window.addEventListener('mouseup', checkIfShouldStartMomentum)
  window.addEventListener('wheel', cancelMomentum)

  const globalActionUnsubscribe = globalStore.$onAction(
    ({ name, args }) => {
      if (name === 'triggerPanningStart') {
        shouldStartPanning = true
      }
    }
  )
  unsubscribes = () => {
    globalActionUnsubscribe()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('mousedown', cancelMomentum)
  window.removeEventListener('mousemove', checkIfShouldStartPanning)
  window.removeEventListener('mouseup', checkIfShouldStartMomentum)
  window.removeEventListener('wheel', cancelMomentum)
  unsubscribes()
})

// handle pointer events

const checkIfShouldStartPanning = (event) => {
  if (globalStore.currentUserIsPanning) {
    event.preventDefault()
    initPanning(event)
    updatePanningPosition(event)
  }
}
const checkIfShouldStartMomentum = () => {
  const isPanning = Boolean(startPosition)
  if (isPanning && panningDelta) {
    startMomentum()
  }
  // clear the pending pan frame alongside its delta, momentum continues from velocity
  shouldPanNextFrame = false
  panningDelta = null
  shouldCancelPanningTimer = true
}
const cancelMomentum = (event) => {
  shouldCancelMomentumTimer = true
  if (event?.type === 'wheel') {
    shouldCancelPanningTimer = true
    startPosition = null
    panningDelta = null
    shouldPanNextFrame = false
  }
}

// safari fix

const updatecurrentScrollByDelta = (delta) => {
  currentScroll.x += delta.x
  currentScroll.y += delta.y
}
const safariFix = () => {
  // force safari to recompute internal element positions after panning
  // https://forum.kinopio.club/t/cursor-position-is-wrong-after-right-click-drag-to-pan/1799
  if (!utils.isSafari()) { return }
  window.scrollTo(currentScroll.x, currentScroll.y)
}

// panning

const initPanning = (event) => {
  const position = utils.cursorPositionInPage(event)
  currentScroll = { x: window.scrollX, y: window.scrollY }
  if (shouldStartPanning) {
    startPosition = position
    shouldStartPanning = false
    shouldCancelPanningTimer = false
    shouldCancelMomentumTimer = false
    panningTimer = window.requestAnimationFrame(panningFrame)
  }
}
const updatePanningPosition = (event) => {
  const position = utils.cursorPositionInPage(event)
  if (startPosition) {
    const delta = {
      x: startPosition.x - position.x,
      y: startPosition.y - position.y
    }
    velocity = { x: delta.x, y: delta.y }
    panningDelta = delta
    shouldPanNextFrame = true
  }
}
const panningFrame = () => {
  // scroll frame
  if (shouldPanNextFrame && panningDelta) {
    window.scrollBy(panningDelta.x, panningDelta.y, 'instant')
    updatecurrentScrollByDelta(panningDelta)
    shouldPanNextFrame = false
  } else if (velocity) {
    // no cursor movement this frame, decay velocity so that pausing
    // before releasing doesn't fling on momentum from an older movement
    velocity.x *= momentumDeceleration
    velocity.y *= momentumDeceleration
  }
  panningTimer = window.requestAnimationFrame(panningFrame)
  // cancel
  if (shouldCancelPanningTimer) {
    window.cancelAnimationFrame(panningTimer)
    panningTimer = null
    startPosition = null
  }
}

// momentum scrolling, post-panning

const startMomentum = () => {
  window.cancelAnimationFrame(momentumTimer)
  const momentumFrame = () => {
    // cancel
    const velocityIsLow = Math.abs(velocity.x) < momentumThreshold && Math.abs(velocity.y) < momentumThreshold
    if (velocityIsLow || shouldPanNextFrame || shouldCancelMomentumTimer) {
      window.cancelAnimationFrame(momentumTimer)
      safariFix()
      return
    }
    // scroll frame
    velocity.x *= momentumDeceleration
    velocity.y *= momentumDeceleration
    window.scrollBy(velocity.x, velocity.y, 'instant')
    updatecurrentScrollByDelta(velocity)
    momentumTimer = window.requestAnimationFrame(momentumFrame)
  }
  momentumTimer = window.requestAnimationFrame(momentumFrame)
}
</script>

<template lang="pug">
</template>

<style lang="stylus">
</style>
